/**
 * Step 9 — Equipment Placement. Imports AI-calc from Step 7 and lets tech confirm per-room placements.
 */
import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Wind, Plus, Trash2, Loader2, Check, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";

export default function Step9Equipment({ workflow, requestId, onAdvance, fetchWorkflow }) {
  const s7 = workflow?.step_7 || {};
  const affectedAreas = workflow?.step_4?.affected_areas || [];

  // Seed placements: one per affected area, equipment totals divided evenly
  const seed = () => {
    const n = Math.max(1, affectedAreas.length);
    const split = (total) => Math.max(1, Math.ceil((total || 0) / n));
    return affectedAreas.map(a => ({
      room: a,
      dehumidifiers: split(s7.equipment_dehumidifiers),
      air_movers: split(s7.equipment_air_movers),
      air_scrubbers: (s7.equipment_air_scrubbers || 0) > 0 ? 1 : 0,
      notes: "",
    }));
  };

  const [placements, setPlacements] = useState(workflow?.step_9?.placements?.length ? workflow.step_9.placements : seed());
  const [saving, setSaving] = useState(false);
  const [showNa, setShowNa] = useState(false);
  const [naReason, setNaReason] = useState("");

  const update = (i, patch) => setPlacements(p => p.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const remove = (i) => setPlacements(p => p.filter((_, idx) => idx !== i));
  const add = () => setPlacements(p => [...p, { room: "", dehumidifiers: 0, air_movers: 0, air_scrubbers: 0, notes: "" }]);

  const save = async () => {
    if (placements.length === 0) return toast.error("Add at least one room");
    for (const p of placements) if (!p.room.trim()) return toast.error("Every placement needs a room");
    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/9/save`, { placements });
      toast.success("Equipment placement confirmed");
      onAdvance(10);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const markNa = async () => {
    if (!naReason.trim()) return toast.error("Reason required");
    try {
      await API.put(`/inspection/${requestId}/step/9/status`, { status: "na", na_reason: naReason.trim() });
      toast.success("Step 9 marked Not Applicable");
      setShowNa(false); fetchWorkflow(); onAdvance(10);
    } catch (err) { toast.error("Could not mark N/A"); }
  };

  return (
    <div className="space-y-4" data-testid="step-9-form">
      <div className="flex items-center gap-2">
        <Wind className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 9 · Equipment Placement</h2>
      </div>

      <Card className="bg-zinc-50">
        <CardContent className="pt-3 pb-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">From Step 7 Calculation</p>
          <p className="text-xs text-zinc-700">
            {s7.equipment_dehumidifiers || 0} dehumidifiers · {s7.equipment_air_movers || 0} air movers · {s7.equipment_air_scrubbers || 0} scrubbers
          </p>
        </CardContent>
      </Card>

      {placements.map((p, i) => (
        <Card key={i} data-testid={`step-9-row-${i}`}>
          <CardContent className="pt-3 pb-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input value={p.room} onChange={e => update(i, { room: e.target.value })} placeholder="Room name"
                     className="h-10 text-sm font-semibold" data-testid={`step-9-room-${i}`} />
              <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-rose-500" data-testid={`step-9-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <NumField label="Dehumid." value={p.dehumidifiers} onChange={n => update(i, { dehumidifiers: n })} testId={`step-9-dehu-${i}`} />
              <NumField label="Movers" value={p.air_movers} onChange={n => update(i, { air_movers: n })} testId={`step-9-mover-${i}`} />
              <NumField label="Scrubbers" value={p.air_scrubbers} onChange={n => update(i, { air_scrubbers: n })} testId={`step-9-scrub-${i}`} />
            </div>
            <Input value={p.notes || ""} onChange={e => update(i, { notes: e.target.value })} placeholder="Notes (optional)"
                   className="h-10 text-xs" />
          </CardContent>
        </Card>
      ))}

      <Button variant="outline" onClick={add} className="w-full h-11" data-testid="step-9-add-row">
        <Plus className="w-4 h-4 mr-1" /> Add Room
      </Button>

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => setShowNa(true)} className="h-12" data-testid="step-9-na-btn">
          <AlertCircle className="w-4 h-4 mr-1" /> Not Applicable
        </Button>
        <Button onClick={save} disabled={saving} className="h-12 bg-emerald-600 text-white" data-testid="step-9-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Mark Complete</>}
        </Button>
      </div>

      {showNa && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" data-testid="step-9-na-dialog">
          <Card className="w-full max-w-sm"><CardContent className="pt-4 space-y-3">
            <div className="flex justify-between"><h3 className="text-sm font-bold">Mark Not Applicable</h3>
              <Button size="sm" variant="ghost" onClick={() => setShowNa(false)}><X className="w-4 h-4" /></Button></div>
            <textarea value={naReason} onChange={e => setNaReason(e.target.value)} placeholder="Reason (required)"
                      className="w-full p-2 text-xs border rounded h-20" data-testid="step-9-na-reason" />
            <Button onClick={markNa} className="w-full h-11 bg-zinc-900 text-white" data-testid="step-9-na-submit">Confirm</Button>
          </CardContent></Card>
        </div>
      )}
    </div>
  );
}

function NumField({ label, value, onChange, testId }) {
  return (
    <div>
      <p className="text-[9px] text-zinc-500">{label}</p>
      <Input type="number" value={value} onChange={e => onChange(parseInt(e.target.value, 10) || 0)}
             className="h-10 text-xs" data-testid={testId} />
    </div>
  );
}
